"use client";

import type { AuthDialogIntent } from "@/components/auth/dialogs/auth-dialog";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { User } from "@/lib/types";
import type { PropsWithChildren } from "react";
import React, { useImperativeHandle } from "react";

export type AuthGuardHandle = {
  trigger: () => void;
};

export function AuthGuard({
  ref,
  children,
  intent,
  onLoggedIn,
  title,
  skipSegmentation,
}: PropsWithChildren<{
  ref?: React.Ref<AuthGuardHandle | null>;
  intent?: AuthDialogIntent;
  onLoggedIn?: (user?: User) => void | Promise<void>;
  title?: string;
  skipSegmentation?: boolean;
}>) {
  const { user, loaded, displayAuthDialog } = useCurrentUser();

  function trigger() {
    if (user) {
      return;
    }

    displayAuthDialog({ intent, onLoggedIn, title, skipSegmentation });
  }

  useImperativeHandle(
    ref,
    () => ({
      trigger,
    }),
    [trigger],
  );

  if (user && loaded) {
    return children;
  }

  function onGuardClick(e?: React.MouseEvent) {
    if (e) {
      e.stopPropagation();
    }

    displayAuthDialog({ intent, onLoggedIn, title, skipSegmentation });
  }

  function onGuardKeyDown(e?: React.KeyboardEvent) {
    if (!e) {
      return;
    }

    e.stopPropagation();
    if (e.key === "Enter") {
      displayAuthDialog({ intent, onLoggedIn, title, skipSegmentation });
    }
  }

  return React.Children.map(children, (child) =>
    React.isValidElement(child)
      ? React.cloneElement(child as React.ReactElement<any>, {
          onClick: onGuardClick,
          onKeyDown: onGuardKeyDown,
        })
      : child,
  );
}

AuthGuard.displayName = "AuthGuard";
